/**
 * DEC-1425 (Tier 2 of DEC-1423): resilient replacement for React.lazy.
 *
 * THE BUG IT FIXES. A stalled dynamic import() on mobile is a promise that
 * never resolves *and never rejects*. React.lazy wraps it, <Suspense> shows
 * its "Loading…" fallback (App.js), and because nothing ever throws,
 * AppErrorBoundary — which only catches thrown/rejected errors — never fires.
 * Result: an infinite "Loading…" with no recovery, the exact mobile-enrollment
 * hang in DEC-1423 (reproduced in Incognito on a cold bundle).
 *
 * WHAT THIS DOES. Wraps the import factory so a hang becomes recoverable:
 *   1. Race each import against a timeout that REJECTS — turning a silent hang
 *      into a real error the app can act on.
 *   2. One silent retry — covers a transient mobile network blip or a chunk
 *      that was mid-publish during a deploy.
 *   3. If it still fails, auto-reload the page ONCE (guarded by a sessionStorage
 *      one-shot so a genuinely-broken deploy can't loop) to pull the fresh
 *      index.html + chunk hashes. On the second failure we rethrow, so the
 *      existing AppErrorBoundary "Something went wrong — Reload" screen takes
 *      over with a manual button.
 *   4. Any successful load clears the one-shot, so the auto-reload is available
 *      again for a future stall.
 *
 * Drop-in for `lazy`: App.js imports it as `{ lazyWithRetry as lazy }`, so every
 * existing `lazy(() => import('./pages/X'))` call becomes resilient unchanged.
 */

import { lazy } from 'react';

// Long enough that a slow-but-working mobile chunk fetch still completes; short
// enough that a truly stalled one doesn't strand the user for minutes.
const IMPORT_TIMEOUT_MS = 15000;
// One-shot guard: survives the reload it triggers (sessionStorage, per tab) but
// not a new tab/session, so a stale deploy can reload at most once per attempt.
const RELOAD_FLAG = 'deco_chunk_autoreloaded';

function hasReloaded() {
  try { return sessionStorage.getItem(RELOAD_FLAG) === '1'; } catch { return false; }
}
function markReloaded() {
  try { sessionStorage.setItem(RELOAD_FLAG, '1'); } catch { /* private mode / blocked */ }
}
function clearReloaded() {
  try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* no-op */ }
}

// Resolve the import, or reject after IMPORT_TIMEOUT_MS. A bare import() that
// hangs never rejects on its own — this is what converts the hang into an error.
function importWithTimeout(factory) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      const err = new Error('ChunkLoadTimeout');
      err.name = 'ChunkLoadError';
      reject(err);
    }, IMPORT_TIMEOUT_MS);
    factory().then(
      (mod) => { if (settled) return; settled = true; clearTimeout(timer); resolve(mod); },
      (err) => { if (settled) return; settled = true; clearTimeout(timer); reject(err); }
    );
  });
}

// The recovery chain, factored out of lazy() so it can be unit-tested without
// rendering a Suspense tree: import → (fail) silent retry → (fail) one guarded
// auto-reload → (already reloaded) rethrow for AppErrorBoundary.
export function loadWithRecovery(factory) {
  return importWithTimeout(factory)
    .then((mod) => { clearReloaded(); return mod; })
    .catch(() =>
      // Silent retry (transient blip / just-deployed chunk).
      importWithTimeout(factory)
        .then((mod) => { clearReloaded(); return mod; })
        .catch((err) => {
          // Still failing. Reload once to fetch the current index + chunks;
          // this is the fix for the stale-bundle-after-deploy case AND a
          // second chance for a cold mobile network.
          if (!hasReloaded()) {
            markReloaded();
            window.location.reload();
            // Never settle — nothing should render in the instant before the
            // reload replaces the document.
            return new Promise(() => {});
          }
          // Reloaded once already and it STILL failed → surface a real error
          // so AppErrorBoundary shows its recoverable "Reload" screen.
          throw err;
        })
    );
}

export function lazyWithRetry(factory) {
  return lazy(() => loadWithRecovery(factory));
}

export default lazyWithRetry;
